<template>
  <section class="premium-section">
    <!-- 인트로 오버레이 -->
    <div class="intro-overlay" :class="{ 'is-fading': introFading, 'is-hidden': introHidden }">
      <img src="@/assets/logo/logo_symbol.png" alt="" class="intro-watermark" />
      <div class="intro-text">
        <span class="intro-sub">TECHNO RAYONE CITY</span>
        <span class="intro-main">Premium</span>
      </div>
    </div>

    <!-- 우측하단 워터마크 로고 -->
    <img src="@/assets/logo/logo_symbol.png" alt="" class="watermark-logo" />

    <!-- 배경 장식 -->
    <div class="bg-decoration">
      <div class="deco-line deco-line-left"></div>
      <div class="deco-line deco-line-right"></div>
    </div>

    <!-- 좌측 세로 텍스트 -->
    <div class="side-text side-text-left">PREMIUM LIVING</div>

    <div class="premium-content">
      <!-- 이미지 영역 -->
      <div class="image-container">
        <!-- 4개 라인 프레임 (은은한 코멧 테일) -->
        <div class="frame-border">
          <svg class="border-svg" viewBox="0 0 540 660" preserveAspectRatio="none">
            <defs>
              <linearGradient id="cometTail" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="var(--rayone-primary)" stop-opacity="0" />
                <stop offset="50%" stop-color="var(--rayone-primary)" stop-opacity="0.15" />
                <stop offset="100%" stop-color="var(--rayone-primary)" stop-opacity="0.5" />
              </linearGradient>
            </defs>
            <!-- 라인 1 (0도 위치) -->
            <rect x="1.5" y="1.5" width="537" height="657" class="border-rect border-rect-1" />
            <!-- 라인 2 (90도 위치) -->
            <rect x="1.5" y="1.5" width="537" height="657" class="border-rect border-rect-2" />
            <!-- 라인 3 (180도 위치) -->
            <rect x="1.5" y="1.5" width="537" height="657" class="border-rect border-rect-3" />
            <!-- 라인 4 (270도 위치) -->
            <rect x="1.5" y="1.5" width="537" height="657" class="border-rect border-rect-4" />
          </svg>
        </div>

        <!-- 비네트 오버레이 -->
        <div class="vignette-overlay"></div>

        <!-- 이미지 (크로스페이드 + Ken Burns) -->
        <div class="image-wrapper">
          <img
            v-for="(item, idx) in items"
            :key="idx"
            :src="images[item.image]"
            :alt="item.title"
            class="premium-image"
            :class="{ 'is-active': currentItem === idx }"
          />
        </div>
      </div>

      <!-- 중앙: 다이아몬드 인디케이터 (세로) -->
      <div class="diamond-indicators">
        <span
          v-for="(item, index) in items"
          :key="index"
          class="diamond"
          :class="{ 'is-active': currentItem === index }"
          @click="goToItem(index)"
        >
          <span class="diamond-inner"></span>
          <span class="diamond-glow"></span>
        </span>
      </div>

      <!-- 우측: 텍스트 영역 -->
      <div class="text-container">
        <!-- 텍스트 콘텐츠 -->
        <div class="text-content">
          <transition name="slide-fade" mode="out-in">
            <div :key="currentItem" class="text-inner">
              <span class="item-eng">{{ items[currentItem].eng }}</span>
              <h2 class="item-title">{{ items[currentItem].title }}</h2>
              <p class="item-desc">{{ items[currentItem].desc }}</p>
            </div>
          </transition>
        </div>

        <!-- 페이지 카운터 -->
        <div class="page-counter">
          <span class="counter-label">SECTION</span>
          <div class="counter-numbers">
            <span class="current">{{ String(currentItem + 1).padStart(2, '0') }}</span>
            <span class="divider">/</span>
            <span class="total">{{ String(items.length).padStart(2, '0') }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// Unsplash 이미지 URL
const imageModules = {
  location: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
  transportation: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&q=80',
  infrastructure: 'https://images.unsplash.com/photo-1567521464027-f127ff144326?w=800&q=80',
  nature: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80'
}

export default {
  name: 'PremiumSection',
  props: {
    items: {
      type: Array,
      required: true
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentItem: 0,
      isTransitioning: false,
      isScrollLocked: false,
      introFading: false,
      introHidden: true,
      introShown: false,
      images: imageModules
    }
  },
  computed: {
    currentImage() {
      return this.images[this.items[this.currentItem].image] || this.images.location
    }
  },
  created() {
    // 이미지 preload
    Object.values(imageModules).forEach(src => {
      const img = new Image()
      img.src = src
    })
  },
  watch: {
    isActive(newVal) {
      if (newVal) {
        this.currentItem = 0
        // 인트로 애니메이션 (처음 한번만)
        if (!this.introShown) {
          this.introHidden = false
          this.isScrollLocked = true

          // 1.5초 후 페이드 시작
          setTimeout(() => {
            this.introFading = true
          }, 1500)

          // 3초 후 완전히 숨김 + 스크롤 락 해제
          setTimeout(() => {
            this.introHidden = true
            this.introShown = true
            this.isScrollLocked = false
          }, 3000)
        }
      }
    }
  },
  methods: {
    handleScroll(direction) {
      if (this.isScrollLocked) return

      if (direction === 'down') {
        if (this.currentItem < this.items.length - 1) {
          this.goToItem(this.currentItem + 1)
        } else {
          this.$emit('next-section')
        }
      } else if (direction === 'up') {
        if (this.currentItem > 0) {
          this.goToItem(this.currentItem - 1)
        } else {
          this.$emit('prev-section')
        }
      }
    },
    goToItem(index) {
      if (index === this.currentItem || this.isScrollLocked) return

      this.isScrollLocked = true
      this.currentItem = index

      // 트랜지션 완료 후 스크롤 락 해제
      setTimeout(() => {
        this.isScrollLocked = false
      }, 1000)
    }
  }
}
</script>

<style scoped>
.premium-section {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 50%, #fafafa 100%);
  overflow: hidden;
}

/* 우측하단 워터마크 로고 - 메가헤더와 동일 위치 */
.watermark-logo {
  position: absolute;
  bottom: -15%;
  right: -10%;
  width: 900px;
  height: auto;
  opacity: 0.04;
  pointer-events: none;
  z-index: 0;
}

/* 배경 장식 라인 */
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
}

.deco-line {
  position: absolute;
  width: 1px;
  height: 100%;
  background: linear-gradient(to bottom, transparent, rgba(163, 32, 53, 0.1), transparent);
}

.deco-line-left {
  left: 10%;
}

.deco-line-right {
  right: 10%;
}

/* 좌측 세로 텍스트 */
.side-text {
  position: absolute;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-family: var(--font-serif);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 8px;
  color: rgba(163, 32, 53, 0.15);
  pointer-events: none;
  z-index: 1;
}

.side-text-left {
  left: 40px;
  top: 50%;
  transform: translateY(-50%) rotate(180deg);
}

/* 메인 콘텐츠 */
.premium-content {
  position: relative;
  max-width: 1400px;
  width: 100%;
  padding: 0 80px;
  display: flex;
  align-items: center;
  gap: 40px;
  z-index: 1;
}

/* 인트로 오버레이 */
.intro-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #112238 0%, #091525 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  clip-path: inset(0 0 0 0);
  transition: clip-path 1.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.intro-overlay.is-hidden {
  visibility: hidden;
  pointer-events: none;
}

.intro-overlay.is-fading {
  clip-path: inset(100% 0 0 100%);
}

.intro-watermark {
  position: absolute;
  bottom: -15%;
  right: -10%;
  width: 900px;
  height: auto;
  opacity: 0.15;
  pointer-events: none;
  filter: brightness(0) invert(1);
  transition: opacity 0.5s ease;
}

.intro-overlay.is-fading .intro-watermark {
  opacity: 0;
}

.intro-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  transition: opacity 0.5s ease;
}

.intro-overlay.is-fading .intro-text {
  opacity: 0;
}

.intro-sub {
  font-family: var(--font-paperlogy);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 8px;
  color: rgba(255, 255, 255, 0.6);
}

.intro-main {
  font-family: var(--font-serif);
  font-size: 120px;
  font-weight: 400;
  font-style: italic;
  color: #fff;
  letter-spacing: 8px;
}

/* 이미지 컨테이너 */
.image-container {
  position: relative;
  flex-shrink: 0;
  width: 500px;
  height: 620px;
}

/* 움직이는 테두리 선 */
.frame-border {
  position: absolute;
  top: -20px;
  left: -20px;
  right: -20px;
  bottom: -20px;
  pointer-events: none;
  z-index: 5;
}

.border-svg {
  width: 100%;
  height: 100%;
}

.border-rect {
  fill: none;
  stroke: url(#cometTail);
  stroke-width: 1;
  stroke-linecap: round;
  stroke-dasharray: 150 447;
  filter: drop-shadow(0 0 3px rgba(163, 32, 53, 0.3));
}

/* 라인 1 - 0도 위치 */
.border-rect-1 {
  animation: borderMove1 12s linear infinite;
}

/* 라인 2 - 90도 위치 (597 = 둘레의 1/4) */
.border-rect-2 {
  animation: borderMove2 12s linear infinite;
}

/* 라인 3 - 180도 위치 (1194 = 둘레의 1/2) */
.border-rect-3 {
  animation: borderMove3 12s linear infinite;
}

/* 라인 4 - 270도 위치 (1791 = 둘레의 3/4) */
.border-rect-4 {
  animation: borderMove4 12s linear infinite;
}

@keyframes borderMove1 {
  0% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -2388; }
}

@keyframes borderMove2 {
  0% { stroke-dashoffset: -597; }
  100% { stroke-dashoffset: -2985; }
}

@keyframes borderMove3 {
  0% { stroke-dashoffset: -1194; }
  100% { stroke-dashoffset: -3582; }
}

@keyframes borderMove4 {
  0% { stroke-dashoffset: -1791; }
  100% { stroke-dashoffset: -4179; }
}

/* 비네트 오버레이 */
.vignette-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(ellipse at center, transparent 40%, rgba(0, 0, 0, 0.3) 100%);
  pointer-events: none;
  z-index: 2;
}

/* 이미지 */
.image-wrapper {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.image-wrapper img {
  position: absolute;
  top: 0;
  left: 0;
}

.premium-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.8s ease;
}

.premium-image.is-active {
  opacity: 1;
  animation: kenBurns 20s ease-in-out infinite alternate;
}

@keyframes kenBurns {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.1);
  }
}

/* 다이아몬드 인디케이터 (세로 배치) */
.diamond-indicators {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  flex-shrink: 0;
  padding: 0 20px;
}

.diamond {
  width: 14px;
  height: 14px;
  position: relative;
  transform: rotate(45deg);
  cursor: pointer;
}

.diamond-inner {
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  background: #d0d0d0;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.diamond-glow {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 1px solid transparent;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.diamond.is-active .diamond-inner {
  background: var(--rayone-primary);
}

.diamond.is-active .diamond-glow {
  border-color: rgba(163, 32, 53, 0.3);
  box-shadow: 0 0 20px rgba(163, 32, 53, 0.4);
}

.diamond:hover:not(.is-active) .diamond-inner {
  background: #999;
}

/* 텍스트 컨테이너 */
.text-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 50px;
  padding-left: 40px;
}

/* 텍스트 콘텐츠 */
.text-content {
  min-height: 300px;
}

.text-inner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.item-eng {
  font-family: var(--font-serif);
  font-size: 64px;
  font-weight: 400;
  font-style: italic;
  background: linear-gradient(135deg, var(--rayone-primary) 0%, #c45a6a 50%, var(--rayone-primary) 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 4px;
  animation: shimmer 4s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% {
    background-position: 0% center;
  }
  50% {
    background-position: 100% center;
  }
}

.item-title {
  font-family: var(--font-paperlogy);
  font-size: 52px;
  font-weight: 700;
  color: var(--text-color);
  letter-spacing: 3px;
}

.item-desc {
  font-size: 18px;
  color: #555;
  line-height: 2.4;
  white-space: pre-line;
  margin-top: 24px;
  position: relative;
  padding-left: 24px;
}

.item-desc::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 3px;
  height: calc(100% - 16px);
  background: linear-gradient(to bottom, var(--rayone-primary), transparent);
}

/* 페이지 카운터 */
.page-counter {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: var(--font-paperlogy);
}

.counter-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 4px;
  color: #999;
}

.counter-numbers {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.page-counter .current {
  font-size: 60px;
  font-weight: 700;
  color: var(--rayone-primary);
}

.page-counter .divider {
  font-size: 28px;
  color: #ccc;
}

.page-counter .total {
  font-size: 28px;
  color: #999;
}

/* 트랜지션 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-leave-active {
  transition: all 0.5s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(40px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
</style>
